import { useCallback, useState } from 'react';
import { useThreads } from '@/hooks/ai-agents/hooks/useThreads';
import { useChatInitialization } from '@/hooks/ai-agents/hooks/useChatInitialization';
import { useUser } from '@/hooks/auth/use-user';
import { useAIAgentChat } from '@/contexts/ai-agents';
import { useAIUsageData } from '@/hooks/subscription/useAIUsageData';
import { useGlobalModals } from '@/contexts/GlobalModalsContext';
import { useLocalStorageState } from '@/hooks/ai-agents/useLocalStorageState';
import { logInfo, logError, LogCategory } from '@/utils/logger';

export const useQuickChat = () => {
  const { user } = useUser();
  const { 
    currentAssistant,
    isGenerating: isAISending
  } = useAIAgentChat();
  const { threads, isLoading: isLoadingThreads } = useThreads();
  const usageData = useAIUsageData();
  const { openOutOfCreditsModal } = useGlobalModals();
  const { 
    setCurrentThreadId, 
    setActiveTab, 
    openModal,
    clearInput,
    setInputValue
  } = useLocalStorageState();
  const [isSendingMessage, setIsSendingMessage] = useState(false);

  // Check if user has any threads
  const hasThreads = threads.length > 0;
  const mostRecentThread = hasThreads ? threads[0] : null; // threads are already sorted by most recent

  // Thread initialization hook for creating new threads
  const { initializeChat } = useChatInitialization(
    null, // No current thread ID
    (newThreadId) => {
      if (newThreadId) {
        setCurrentThreadId(newThreadId);
      }
    },
    () => {}, // Messages will be handled by the AI chat interface
    async () => {}, // Message loading will be handled by AI chat interface
    user,
    currentAssistant
  );

  /**
   * Enhanced Send Message - Opens thread and auto-sends message
   * 
   * Flow:
   * 1. Check credits
   * 2. If threads exist: Open most recent thread + auto-send message
   * 3. If no threads: Create new thread + auto-send message  
   * 4. Open modal to show the conversation
   * 
   * ✅ FIXED: Simplified flow with direct thread ID passing to eliminate race conditions
   */
  const handleSendMessage = useCallback(async (message: string) => {
    if (!user) {
      logError('Cannot send message: user not authenticated', {
        category: LogCategory.USER_ACTION
      });
      return;
    }

    // Check credits upfront
    if (usageData.remainingBalance === 0) {
      logInfo('Insufficient credits - showing modal', {
        category: LogCategory.USER_ACTION
      });
      openOutOfCreditsModal();
      return;
    }

    setIsSendingMessage(true);

    try {
      let targetThreadId: string | null = null;

      if (hasThreads && mostRecentThread) {
        // ✅ EXISTING THREAD FLOW: Use most recent thread
        logInfo('Using existing most recent thread for Quick Chat', {
          category: LogCategory.USER_ACTION,
          data: { 
            threadId: mostRecentThread.id,
            threadTitle: mostRecentThread.lastMsgFromUser,
            messageLength: message.length
          }
        });

        targetThreadId = mostRecentThread.id;
        
        // Step 1: Set the thread ID first
        setCurrentThreadId(targetThreadId);
        
        // Step 2: Open modal and switch to chat tab
        setActiveTab('chat');
        openModal();

        // Step 3: Wait for modal to initialize and input to be ready
        await new Promise(resolve => setTimeout(resolve, 200));
        
        // Step 4: Set input value
        setInputValue(message);
        
        // Step 5: Wait for input to be ready (simplified)
        await new Promise(resolve => setTimeout(resolve, 200));

        // Step 6: Trigger the send button programmatically instead of manual API call
        window.dispatchEvent(new CustomEvent('copilot-trigger-send'));

        // Step 7: Clear input after successful send (UI will handle this automatically)
        // clearInput(); // Remove this as UI handles clearing

        logInfo('Quick Chat message sent to existing thread successfully', {
          category: LogCategory.USER_ACTION,
          data: { threadId: targetThreadId }
        });

      } else {
        // ✅ NEW THREAD FLOW: Create new thread first, then proceed
        logInfo('Creating new thread for Quick Chat', {
          category: LogCategory.USER_ACTION,
          data: { messageLength: message.length }
        });

        // Step 1: Create new thread and wait for completion
        const newThreadId = await initializeChat();
        
        if (!newThreadId) {
          throw new Error('Failed to create new thread');
        }

        targetThreadId = newThreadId;

        logInfo('New thread created for Quick Chat', {
          category: LogCategory.USER_ACTION,
          data: { threadId: newThreadId }
        });

        // Step 2: Set thread context
        setCurrentThreadId(targetThreadId);

        // Step 3: Open modal and switch to chat tab
        setActiveTab('chat');
        openModal();

        // Step 4: Wait for modal and thread context to be fully ready
        // This is critical for new threads as the AI chat context needs to initialize
        await new Promise(resolve => setTimeout(resolve, 400));

        // Step 5: Set input value
        setInputValue(message);
        
        // Step 6: Wait for input to be ready (simplified)
        await new Promise(resolve => setTimeout(resolve, 200));

        logInfo('Auto-sending message to new thread via AI chat system', {
          category: LogCategory.USER_ACTION,
          data: { 
            threadId: targetThreadId,
            messageLength: message.length
          }
        });

        // Step 7: Trigger the send button programmatically instead of manual API call
        window.dispatchEvent(new CustomEvent('copilot-trigger-send'));

        // Step 8: Clear input after successful send (UI will handle this automatically)
        // clearInput(); // Remove this as UI handles clearing

        logInfo('Quick Chat message sent to new thread successfully', {
          category: LogCategory.USER_ACTION,
          data: { threadId: targetThreadId }
        });
      }

    } catch (error: any) {
      logError('Failed to send Quick Chat message', {
        category: LogCategory.USER_ACTION,
        data: { error: error.message }
      });

      // Revert UI state on error
      setCurrentThreadId(undefined);
      
      // Show user-friendly error
      console.error('Quick Chat error:', error.message);
    } finally {
      setIsSendingMessage(false);
    }
  }, [
    user,
    usageData.remainingBalance,
    openOutOfCreditsModal,
    hasThreads,
    mostRecentThread,
    initializeChat,
    setCurrentThreadId,
    setInputValue,
    setActiveTab,
    openModal,
    clearInput,
  ]);

  /**
   * Handle opening an existing thread (unchanged)
   */
  const handleOpenThread = useCallback((threadId: string) => {
    logInfo('Opening existing thread from Quick Chat', {
      category: LogCategory.USER_ACTION,
      data: { threadId }
    });

    // Set the thread ID and switch to chat tab
    setCurrentThreadId(threadId);
    setActiveTab('chat');
    
    // Open the AI chat modal
    openModal();
  }, [setCurrentThreadId, setActiveTab, openModal]);

  /**
   * Handle viewing all threads (unchanged)
   */
  const handleViewAllThreads = useCallback(() => {
    logInfo('Opening threads view from Quick Chat', {
      category: LogCategory.USER_ACTION
    });

    // Switch to threads tab and open modal
    setActiveTab('threads');
    openModal();
  }, [setActiveTab, openModal]);

  return {
    // State
    hasThreads,
    threads: threads.slice(0, 3), // Show only first 3 threads for space
    isLoadingThreads,
    isSendingMessage: isSendingMessage || isAISending, // Include AI sending state
    
    // Actions
    handleSendMessage,
    handleOpenThread,
    handleViewAllThreads
  };
}; 